/**
 * THE ONE READING OF BRIEFING TEXT. Pure, no imports: the card page draws these
 * blocks (`web/src/screens/safeMarkdown.tsx`), and the checklist and the Ops
 * readability flag (`intake-review.ts`) read the same ones, so "a table" and "a
 * version note" mean one thing everywhere.
 *
 * All of it runs when the text is read. The stored briefing is never rewritten
 * and no model is involved: fixed rules only.
 *
 * @module portal/briefing-blocks
 */

export type TableAlign = "left" | "center" | "right" | null;

export type Block =
  | { kind: "heading"; text: string }
  | { kind: "quote"; text: string }
  | { kind: "list"; ordered: boolean; items: string[] }
  | { kind: "table"; align: TableAlign[]; header: string[]; rows: string[][] }
  | { kind: "paragraph"; text: string };

const HEADING_RE = /^#{1,6}\s+(.+)$/;
const BULLET_RE = /^[-*•]\s+(.+)$/;
const NUMBERED_RE = /^\d+[.)]\s+(.+)$/;
const DELIMITER_CELL_RE = /^:?-+:?$/;

/** A GFM row's cells: outer pipes dropped, `\|` is a pipe in the cell. */
function splitCells(line: string): string[] {
  const inner = line.trim().replace(/^\|/, "").replace(/(?<!\\)\|$/, "");
  return inner.split(/(?<!\\)\|/).map((cell) => cell.trim().replace(/\\\|/g, "|"));
}

function hasPipe(line: string): boolean {
  return /(?<!\\)\|/.test(line);
}

function isDelimiterRow(line: string): boolean {
  return hasPipe(line) && splitCells(line).every((cell) => DELIMITER_CELL_RE.test(cell));
}

function isTableStart(lines: readonly string[], i: number): boolean {
  const header = (lines[i] ?? "").trim();
  const delimiter = (lines[i + 1] ?? "").trim();
  if (!hasPipe(header) || !isDelimiterRow(delimiter)) return false;
  return splitCells(header).length === splitCells(delimiter).length;
}

function alignOf(cell: string): TableAlign {
  const left = cell.startsWith(":");
  const right = cell.endsWith(":");
  return left && right ? "center" : left ? "left" : right ? "right" : null;
}

/**
 * The table starting at `i`, and the line after it. One step off GFM on purpose:
 * a line with no pipe ends the table instead of becoming a one-cell row, because
 * real scripts run "Totals: 40 words" straight under a table.
 */
function readTable(lines: readonly string[], i: number): { table: Extract<Block, { kind: "table" }>; next: number } {
  const header = splitCells((lines[i] ?? "").trim());
  const align = splitCells((lines[i + 1] ?? "").trim()).map(alignOf);
  const rows: string[][] = [];
  let at = i + 2;
  while (at < lines.length) {
    const line = (lines[at] ?? "").trim();
    if (!line || !hasPipe(line) || HEADING_RE.test(line) || line.startsWith(">")) break;
    rows.push(splitCells(line));
    at++;
  }
  return { table: { kind: "table", align, header, rows }, next: at };
}

export function parseMarkdownBlocks(text: string): Block[] {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = (lines[i] ?? "").trim();
    if (!line) {
      i++;
      continue;
    }
    const heading = HEADING_RE.exec(line);
    if (heading) {
      blocks.push({ kind: "heading", text: heading[1]!.trim() });
      i++;
      continue;
    }
    if (line.startsWith(">")) {
      const quoteLines: string[] = [];
      while (i < lines.length && (lines[i] ?? "").trim().startsWith(">")) {
        quoteLines.push((lines[i] ?? "").trim().replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ kind: "quote", text: quoteLines.join(" ") });
      continue;
    }
    if (isTableStart(lines, i)) {
      const { table, next } = readTable(lines, i);
      blocks.push(table);
      i = next;
      continue;
    }
    const numbered = NUMBERED_RE.exec(line);
    const bullet = !numbered ? BULLET_RE.exec(line) : null;
    if (numbered || bullet) {
      const ordered = !!numbered;
      const items: string[] = [(numbered ?? bullet)![1]!.trim()];
      i++;
      while (i < lines.length) {
        const l = (lines[i] ?? "").trim();
        const m = ordered ? NUMBERED_RE.exec(l) : BULLET_RE.exec(l);
        if (!m) break;
        items.push(m[1]!.trim());
        i++;
      }
      blocks.push({ kind: "list", ordered, items });
      continue;
    }
    const paraLines: string[] = [];
    while (i < lines.length) {
      const l = (lines[i] ?? "").trim();
      if (!l || HEADING_RE.test(l) || l.startsWith(">") || BULLET_RE.test(l) || NUMBERED_RE.test(l) || isTableStart(lines, i)) break;
      paraLines.push(l);
      i++;
    }
    // The line breaks stay (the card page wraps with pre-line): a script is often written line by line, and joined it reads as a wall.
    blocks.push({ kind: "paragraph", text: paraLines.join("\n") });
  }
  return blocks;
}

// ------------------------------------------------------------
// The version note
// ------------------------------------------------------------

/**
 * A script writer's bookkeeping at the very top of a card (the Lior BF26 batch),
 * folded away on the card page and kept out of the checklist. Tight by design,
 * since a real briefing line must never be hidden: each rule is the writer's exact
 * wording from the start of the line, and only the head of the text is searched.
 */
const VERSION_NOTE_LINES: readonly RegExp[] = [
  /^CURRENT EDITOR SCRIPT\b.*\bSupersedes\b/,
  /^REVIEWED SCRIPT HANDOFF\b/,
  /^Internal [A-Z0-9]+-SCRIPTS-\d+\b/,
  /^Version \d+\.\d+ \(\d{4}-\d{2}-\d{2}\b[^)]*\)\.(?:\s+Status:.*)?$/,
];
/** "Status:" alone is ordinary English, so it counts only directly under a version-note line. */
const VERSION_NOTE_STATUS_RE = /^Status:\s/;
/** How many non-empty lines count as the head. */
const VERSION_NOTE_HEAD_LINES = 12;

/** Where the head ends for certain: a section heading, a table, a list or a quote. A `# title` is part of the head. */
function endsVersionNoteHead(line: string): boolean {
  return /^#{2,6}\s/.test(line) || line.startsWith("|") || line.startsWith(">") || BULLET_RE.test(line) || NUMBERED_RE.test(line);
}

/** The note's lines and the briefing without them. No note: `body` is the text exactly as given. */
export function splitVersionNote(text: string): { note: string[]; body: string } {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const noteAt = new Set<number>();
  let seen = 0;
  for (let i = 0; i < lines.length && seen < VERSION_NOTE_HEAD_LINES; i++) {
    const line = (lines[i] ?? "").trim();
    if (!line) continue;
    seen++;
    if (endsVersionNoteHead(line) || isTableStart(lines, i)) break;
    if (VERSION_NOTE_LINES.some((re) => re.test(line)) || (noteAt.has(i - 1) && VERSION_NOTE_STATUS_RE.test(line))) noteAt.add(i);
  }
  if (noteAt.size === 0) return { note: [], body: text };
  return {
    note: [...noteAt].map((i) => (lines[i] ?? "").trim()),
    body: lines.filter((_, i) => !noteAt.has(i)).join("\n").trim(),
  };
}

// ------------------------------------------------------------
// Long briefings: top-level sections, folded on the card page
// ------------------------------------------------------------

/**
 * When a briefing folds into sections, in one place. It folds when it has at least
 * two sections and is either long, or has many sections without being short.
 */
export const BRIEFING_SECTIONS_CONFIG = {
  /** This many characters is a long briefing, whatever its shape. The Lior BF26 cards run 9,000 to 12,000. */
  longChars: 4000,
  /** This many sections is many, on anything longer than `shortChars`. */
  manySections: 6,
  /** Below this it is short and renders as a plain page, however many small headings it has. */
  shortChars: 1500,
} as const;

/** Besides the first section, these stay open: the editor reads the script first. */
const SCRIPT_HEADING_RE = /\b(?:script|scripts|skript|skripte|drehbuch|sprechertext|voice[- ]?over)\b/i;

export interface BriefingSection {
  /** The heading as written, numbering included. */
  heading: string;
  /** Everything up to the next section, nested headings included. */
  body: string;
  open: boolean;
}

const HEADING_LEVEL_RE = /^(#{1,6})\s+(.+)$/;

/**
 * How the card page folds this text, or null to render it as one page: short, or
 * no heading level repeats, so there are no sections to fold. A section starts at
 * the shallowest heading level that occurs twice, so one `# title` above `## 1.`,
 * `## 2.` stays visible with what follows it. Only ever a view: `intro` and the
 * sections together are the whole text, and the stored briefing is not touched.
 */
export function planBriefingSections(body: string): { intro: string; sections: BriefingSection[] } | null {
  const { longChars, manySections, shortChars } = BRIEFING_SECTIONS_CONFIG;
  if (body.length < shortChars) return null;
  const lines = body.replace(/\r\n/g, "\n").split("\n");
  const headings = lines.flatMap((line, at) => {
    const m = HEADING_LEVEL_RE.exec(line.trim());
    return m ? [{ at, level: m[1]!.length, text: m[2]!.trim() }] : [];
  });
  const count = (level: number) => headings.filter((h) => h.level === level).length;
  const sectionLevel = [1, 2, 3, 4, 5, 6].find((level) => count(level) >= 2);
  if (sectionLevel === undefined) return null;
  const starts = headings.filter((h) => h.level <= sectionLevel && h.at >= headings.find((x) => x.level === sectionLevel)!.at);
  if (starts.length < 2 || (starts.length < manySections && body.length < longChars)) return null;
  return {
    intro: lines.slice(0, starts[0]!.at).join("\n"),
    sections: starts.map((h, i) => ({
      heading: h.text,
      body: lines.slice(h.at + 1, starts[i + 1]?.at ?? lines.length).join("\n"),
      open: i === 0 || SCRIPT_HEADING_RE.test(h.text),
    })),
  };
}

// ------------------------------------------------------------
// Readability: what Ops is told about (flag only, `intake-review.ts`)
// ------------------------------------------------------------

/** Every threshold of the readability flag, in one place. */
export const BRIEFING_READABILITY_CONFIG = {
  /**
   * One line of at least this many characters, no break anywhere in it, is a wall
   * of text on the card page. Measured against all 36 open cards on 2026-11-03: the
   * longest real line was 1,065 characters, so this catches only what is truly unbroken.
   */
  wallOfTextMinChars: 1500,
} as const;

export type BriefingReadabilityReason = "broken_table" | "wall_of_text";

function hasBrokenTable(lines: readonly string[]): boolean {
  const isPipeRow = (line: string | undefined) => {
    const l = (line ?? "").trim();
    return l.startsWith("|") && l.slice(1).search(/(?<!\\)\|/) >= 0;
  };
  let i = 0;
  while (i < lines.length) {
    if (isTableStart(lines, i)) {
      const { table, next } = readTable(lines, i);
      if (table.rows.some((row) => row.length !== table.header.length)) return true;
      i = next;
      continue;
    }
    // Rows with no usable delimiter row under the first one: a table that will not parse.
    if (isPipeRow(lines[i]) && isPipeRow(lines[i + 1])) return true;
    i++;
  }
  return false;
}

/** Why a briefing reads badly on the card page, if it does. The version note is not the briefing and does not count. */
export function assessBriefingReadability(text: string | null | undefined): BriefingReadabilityReason[] {
  if (!text?.trim()) return [];
  const { body } = splitVersionNote(text);
  const reasons: BriefingReadabilityReason[] = [];
  if (hasBrokenTable(body.split("\n"))) reasons.push("broken_table");
  if (parseMarkdownBlocks(body).some((b) => b.kind === "paragraph" && b.text.split("\n").some((line) => line.length >= BRIEFING_READABILITY_CONFIG.wallOfTextMinChars))) reasons.push("wall_of_text");
  return reasons;
}
